import React, { useEffect, useState, useCallback } from 'react';
import { Trophy, Fire, Sparkle, Plus, Minus } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const OverviewTab = ({ api, kids, activeKid, onChanged }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [reportCard, setReportCard] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [pointsForm, setPointsForm] = useState({ delta: '', reason: '' });
  const [config, setConfig] = useState({ points_to_rupee: 0.5 });
  const [cashPoints, setCashPoints] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [lb, lg, cf] = await Promise.all([
        api.get('/kids/leaderboard'),
        activeKid ? api.get(`/kids/${activeKid.kid_id}/ledger`) : Promise.resolve({ data: [] }),
        api.get('/kids/config'),
      ]);
      setLeaderboard(lb.data);
      setLedger(lg.data);
      setConfig(cf.data);
    } catch (e) { console.error(e); }
  }, [api, activeKid]);

  useEffect(() => { fetchData(); setReportCard(null); }, [fetchData]);

  const loadReportCard = async () => {
    if (!activeKid) return;
    setLoadingReport(true);
    try { const r = await api.get(`/kids/${activeKid.kid_id}/report-card`); setReportCard(r.data); }
    catch (e) { toast.error('Failed to load report card'); }
    setLoadingReport(false);
  };

  const adjustPoints = async (sign) => {
    const delta = sign * Math.abs(parseInt(pointsForm.delta || 0));
    if (!delta || !activeKid) return;
    try {
      await api.post(`/kids/${activeKid.kid_id}/points`, { delta, reason: pointsForm.reason });
      toast.success(`${delta > 0 ? '+' : ''}${delta} points for ${activeKid.name}`);
      setPointsForm({ delta: '', reason: '' });
      fetchData(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const cashout = async () => {
    const pts = parseInt(cashPoints || 0);
    if (!pts || !activeKid) return;
    try {
      const r = await api.post(`/kids/${activeKid.kid_id}/cashout`, { points: pts });
      toast.success(`₹${r.data.rupees} pocket money for ${activeKid.name} — logged in Cashbook`);
      setCashPoints('');
      fetchData(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveRate = async (val) => {
    const r = await api.put('/kids/config', { points_to_rupee: val });
    setConfig(r.data);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="kids-overview-tab">
      <div className="glass-card p-5">
        <h3 className="font-semibold text-sm flex items-center gap-2 mb-4"><Trophy size={16} className="text-amber-500" /> Weekly Leaderboard</h3>
        <div className="space-y-3">
          {leaderboard.map((k, i) => (
            <div key={k.kid_id} className="flex items-center gap-3" data-testid={`leaderboard-row-${k.kid_id}`}>
              <span className="text-lg w-6">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}</span>
              <span className="text-xl">{k.avatar_emoji}</span>
              <div className="flex-1">
                <p className="text-sm font-medium">{k.name}{(k.handicap || 1) !== 1 && <span className="text-[10px] text-purple-500 ml-1">×{k.handicap}</span>}</p>
                <p className="text-xs text-muted-foreground">Total: ⭐ {k.points_balance || 0}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-amber-600">{k.adjusted}{(k.handicap || 1) !== 1 && <span className="text-[10px] font-normal text-muted-foreground"> ({k.week_earned})</span>}</p>
                <p className="text-[10px] text-muted-foreground">
                  {k.week_earned > k.prev_week_earned ? '📈' : k.week_earned < k.prev_week_earned ? '📉' : '➖'} vs last wk {k.prev_week_earned}
                  {k.streak > 0 && <span className="text-orange-500 ml-1"><Fire size={10} weight="fill" className="inline" />{k.streak}d</span>}
                </p>
              </div>
            </div>
          ))}
          {leaderboard.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No kids yet</p>}
        </div>
      </div>

      <div className="glass-card p-5">
        <h3 className="font-semibold text-sm mb-4">Award / Deduct Points {activeKid && <span className="text-muted-foreground font-normal">— {activeKid.name}</span>}</h3>
        <div className="space-y-3">
          <input data-testid="points-amount-input" type="number" min="1" placeholder="Points (e.g. 20)" value={pointsForm.delta}
            onChange={e => setPointsForm({ ...pointsForm, delta: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" />
          <input data-testid="points-reason-input" placeholder="Reason (e.g. Helped grandma)" value={pointsForm.reason}
            onChange={e => setPointsForm({ ...pointsForm, reason: e.target.value })}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm" />
          <div className="flex gap-2">
            <button onClick={() => adjustPoints(1)} data-testid="award-points-btn"
              className="flex-1 px-3 py-2 rounded-full bg-green-600 text-white text-sm font-semibold flex items-center justify-center gap-1"><Plus size={14} /> Award</button>
            <button onClick={() => adjustPoints(-1)} data-testid="deduct-points-btn"
              className="flex-1 px-3 py-2 rounded-full bg-red-500/90 text-white text-sm font-semibold flex items-center justify-center gap-1"><Minus size={14} /> Deduct</button>
          </div>
          <div className="pt-3 border-t border-border">
            <p className="text-xs font-semibold mb-1.5">💵 Pocket Money Cashout
              <span className="font-normal text-muted-foreground ml-1">
                (1 pt = ₹<input type="number" step="0.1" min="0.01" value={config.points_to_rupee}
                  onChange={e => setConfig({ ...config, points_to_rupee: e.target.value })}
                  onBlur={e => saveRate(e.target.value)} data-testid="rate-input"
                  className="w-14 px-1 py-0.5 rounded border border-border bg-background text-xs inline" />)
              </span>
            </p>
            <div className="flex gap-2">
              <input type="number" min="1" value={cashPoints} onChange={e => setCashPoints(e.target.value)} data-testid="cashout-points-input"
                placeholder="Points" className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" />
              <button onClick={cashout} data-testid="cashout-btn"
                className="px-3 py-2 rounded-full bg-amber-500 text-white text-sm font-semibold whitespace-nowrap">
                → ₹{cashPoints ? (cashPoints * config.points_to_rupee).toFixed(0) : '0'}
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Deducts points & logs a Cashbook expense under "Kids Pocket Money"</p>
          </div>
        </div>
        <h4 className="font-semibold text-xs text-muted-foreground mt-5 mb-2">Recent activity</h4>
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {ledger.slice(0, 10).map(e => (
            <div key={e.entry_id} className="flex items-center justify-between text-xs">
              <span className="truncate flex-1 text-muted-foreground">{e.reason}</span>
              <span className={`font-semibold ml-2 ${e.delta > 0 ? 'text-green-600' : 'text-red-500'}`}>{e.delta > 0 ? '+' : ''}{e.delta}</span>
            </div>
          ))}
          {ledger.length === 0 && <p className="text-xs text-muted-foreground">No activity yet</p>}
        </div>
      </div>

      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm flex items-center gap-2"><Sparkle size={16} className="text-purple-500" /> AI Report Card</h3>
          <button onClick={loadReportCard} disabled={loadingReport || !activeKid} data-testid="report-card-btn"
            className="text-xs px-3 py-1.5 rounded-full bg-purple-600 text-white font-semibold disabled:opacity-50">
            {loadingReport ? 'Thinking…' : 'Generate'}
          </button>
        </div>
        {reportCard ? (
          <div className="space-y-3" data-testid="report-card-content">
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="bg-muted/50 rounded-lg p-2"><p className="text-lg font-bold">{reportCard.stats.tasks_completed}</p><p className="text-[10px] text-muted-foreground">Tasks done</p></div>
              <div className="bg-muted/50 rounded-lg p-2"><p className="text-lg font-bold text-amber-600">+{reportCard.stats.points_earned}</p><p className="text-[10px] text-muted-foreground">Points earned</p></div>
              <div className="bg-muted/50 rounded-lg p-2"><p className="text-lg font-bold text-orange-500">{reportCard.stats.streak}🔥</p><p className="text-[10px] text-muted-foreground">Day streak</p></div>
              <div className="bg-muted/50 rounded-lg p-2"><p className="text-lg font-bold">{reportCard.stats.doses_taken}/{reportCard.stats.doses_total}</p><p className="text-[10px] text-muted-foreground">Medicines taken</p></div>
            </div>
            {reportCard.ai_summary && <p className="text-sm leading-relaxed bg-purple-500/5 border border-purple-500/20 rounded-lg p-3">{reportCard.ai_summary}</p>}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Generate a weekly summary of {activeKid?.name || 'your kid'}'s tasks, points, streak and medicine adherence — with an AI-written note.</p>
        )}
      </div>
    </div>
  );
};
